"use client";

import { db, getClientFunctions } from "@/lib/firebase-client";
import type { FirestoreTimestamp, InvitationStatus, RecipeBoxInvitation, RecipeBoxRole } from "@/lib/types";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type CollectionReference,
  type FirestoreError,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const RECIPE_BOX_INVITATIONS_COLLECTION = "invitations";

/**
 * IMPORTANT:
 * Do NOT create Firestore refs at module scope.
 * Next.js can evaluate client modules during prerender/SSR, and `db` may not be initialized then.
 */
function getRecipeBoxInvitationsCollection(): CollectionReference<RecipeBoxInvitationFirestoreDocument> {
  return collection(db, RECIPE_BOX_INVITATIONS_COLLECTION) as CollectionReference<RecipeBoxInvitationFirestoreDocument>;
}

type RecipeBoxInvitationFirestoreDocument = {
  boxId: string;
  senderUid: string;
  email: string;
  recipientName?: string;
  role: RecipeBoxRole;
  status: InvitationStatus;
  token?: string;
  invitedUserUid?: string;
  createdAt?: FirestoreTimestamp;
  expiresAt?: FirestoreTimestamp;
  acceptedAt?: FirestoreTimestamp;
  declinedAt?: FirestoreTimestamp;
  revokedAt?: FirestoreTimestamp;
  resentAt?: FirestoreTimestamp[];
};

const mapInvitation = (
  docSnapshot: QueryDocumentSnapshot<RecipeBoxInvitationFirestoreDocument>,
): RecipeBoxInvitation => {
  const data = docSnapshot.data();
  return {
    id: docSnapshot.id,
    recipeBoxId: data.boxId,
    inviterUid: data.senderUid,
    recipientEmail: data.email,
    recipientName: data.recipientName,
    role: data.role,
    status: data.status,
    token: data.token,
    invitedUserUid: data.invitedUserUid,
    createdAt: data.createdAt,
    expiresAt: data.expiresAt,
    acceptedAt: data.acceptedAt,
    cancelledAt: data.revokedAt ?? data.declinedAt,
  };
};

const toLowerCaseEmail = (value: string): string => value.trim().toLowerCase();

export function subscribeToRecipeBoxInvitations(
  recipeBoxId: string,
  onUpdate: (invitations: RecipeBoxInvitation[]) => void,
  onError?: (error: FirestoreError) => void,
): () => void {
  const invitationsQuery = query(
    getRecipeBoxInvitationsCollection(),
    where("boxId", "==", recipeBoxId),
    orderBy("createdAt", "desc"),
  );

  return onSnapshot(
    invitationsQuery,
    (snapshot) => {
      const invitations = snapshot.docs.map((docSnapshot) => mapInvitation(docSnapshot));
      onUpdate(invitations);
    },
    onError,
  );
}

export function subscribeToPendingInvitationsByEmail(
  email: string,
  onUpdate: (invitations: RecipeBoxInvitation[]) => void,
  onError?: (error: FirestoreError) => void,
): () => void {
  const normalizedEmail = toLowerCaseEmail(email);

  const invitationsQuery = query(
    getRecipeBoxInvitationsCollection(),
    where("email", "==", normalizedEmail),
    where("status", "==", "pending" satisfies InvitationStatus),
    orderBy("createdAt", "desc"),
  );

  return onSnapshot(
    invitationsQuery,
    (snapshot) => {
      const invitations = snapshot.docs.map((docSnapshot) => mapInvitation(docSnapshot));
      onUpdate(invitations);
    },
    onError,
  );
}


export type InviteRecipeBoxMemberInput = {
  recipeBoxId: string;
  email: string;
  recipientName?: string;
  role: RecipeBoxRole;
};

export async function inviteRecipeBoxMember(input: InviteRecipeBoxMemberInput): Promise<void> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<
    { boxId: string; email: string; recipientName?: string; role: RecipeBoxRole },
    { success?: boolean; inviteId?: string }
  >(functions, "inviteMember");

  const response = await callable({
    boxId: input.recipeBoxId,
    email: input.email,
    recipientName: input.recipientName,
    role: input.role
  });

  if (!response.data?.success) {
    throw new Error("Invitation request did not complete successfully");
  }
}

export async function cancelRecipeBoxInvitation(invitationId: string): Promise<void> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<{ inviteId: string }, { success?: boolean }>(functions, "cancelInvitation");
  await callable({ inviteId: invitationId });
}

export async function resendRecipeBoxInvitation(invitationId: string): Promise<void> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<{ inviteId: string }, { success?: boolean }>(functions, "resendInvitation");
  await callable({ inviteId: invitationId });
}

export async function removeRecipeBoxMember(recipeBoxId: string, memberUid: string): Promise<void> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<
    { boxId: string; memberUid: string },
    { success?: boolean }
  >(functions, "removeMember");

  await callable({ boxId: recipeBoxId, memberUid });
}

export async function updateRecipeBoxMemberRole(
  recipeBoxId: string,
  memberUid: string,
  newRole: RecipeBoxRole,
): Promise<void> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<
    { boxId: string; memberUid: string; newRole: RecipeBoxRole },
    { success?: boolean }
  >(functions, "updateMemberRole");

  await callable({ boxId: recipeBoxId, memberUid, newRole });
}

type HandleInviteResponseResult =
  | { success: true; action: "accepted"; boxId: string }
  | { success: true; action: "declined" };

const getRespondCallable = () => {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  return httpsCallable<{ token: string; accept: boolean }, HandleInviteResponseResult>(
    functions,
    "handleInviteResponse",
  );
};

async function respondToInvitation(token: string, accept: boolean): Promise<HandleInviteResponseResult> {
  if (!token) {
    throw new Error("Invitation token is required");
  }

  const callable = getRespondCallable();
  const response = await callable({ token, accept });
  const result = response.data;

  if (!result?.success) {
    throw new Error("Invitation response failed");
  }

  return result;
}

export async function acceptRecipeBoxInvitation(token: string): Promise<HandleInviteResponseResult> {
  return respondToInvitation(token, true);
}

export async function declineRecipeBoxInvitation(token: string): Promise<HandleInviteResponseResult> {
  return respondToInvitation(token, false);
}
